import React, { Component } from 'react';
import {
	Modal,
	Form,
	FormField,
	Input,
	DatePicker,
	Select,
	Checkbox,
	SpaceBetween,
	Button,
	Alert,
	Container,
	Box,
} from '@cloudscape-design/components';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { configStore } from '../stores/config';
import { bonusesStore } from '../stores/bonuses';
import { bonusesAddModalStore } from '../stores/bonusesAddModal';
import DateRangePicker from "@cloudscape-design/components/date-range-picker";
import Grid from "@cloudscape-design/components/grid";
import cloneDeep from "lodash/cloneDeep";
import Spinner from "@cloudscape-design/components/spinner";

const createEmptyAdPayment = () => ({
	date: "",
	amount: "",
});

const normalizeAdPayments = (payments = []) => {
	if (!Array.isArray(payments)) {
		return [];
	}

	return payments.map((payment) => ({
		date: payment?.date || "",
		amount: payment?.amount ?? "",
	}));
};

const calculateAdPaymentsTotal = (payments = []) => {
	const normalizedPayments = normalizeAdPayments(payments);
	let hasAmount = false;

	const total = normalizedPayments.reduce((sum, payment) => {
		if (payment.amount === "" || payment.amount === null || typeof payment.amount === "undefined") {
			return sum;
		}

		const amount = Number(payment.amount);
		if (Number.isNaN(amount)) {
			return sum;
		}

		hasAmount = true;
		return sum + amount;
	}, 0);

	return hasAmount ? String(total) : "";
};

class BonusesAddModal extends Component {
	constructor(props) {
		super(props);

		this.state = {
			loading: false,
		}
	}

	getActivationLifetimeDaysError = (value) => {
		if (value === '' || value === null || typeof value === 'undefined') {
			return null;
		}

		const activationLifetimeDays = Number(value);
		if (!Number.isInteger(activationLifetimeDays) || activationLifetimeDays <= 0) {
			return 'Life time promo days must be a positive whole number.';
		}

		return null;
	};


	validateField = (field, value) => {
		const { bonusesAddModal, setBonusesAddModal } = this.props;

		const errors = { ...bonusesAddModal.errors };
		const latinNoSpaces = /^[a-zA-Z0-9]+$/;
		const latinWithUnderscore = /^[a-zA-Z0-9]+(_[a-zA-Z0-9]+)*$/;

		switch (field) {
			case 'bonusCode':
				if (bonusesAddModal.bonusCode.length) {
					if (!latinNoSpaces.test(value) || value.length > 20) {
						errors.bonusCode = 'bonusCode Code must be 20 characters max, only Latin letters and numbers, no spaces.';
					} else {
						delete errors.bonusCode;
					}
				} else {
					delete errors.bonusCode;
				}
				break;
			case 'name':
				if (bonusesAddModal.name.length) {

					if (!latinNoSpaces.test(value) || value.length > 16) {
						errors.name = 'Name must be 16 characters max, only Latin letters and numbers, no spaces.';
					} else {
						delete errors.name;
					}
				} else {
					delete errors.name;
				}
				break;
			case 'reasonIdentifier':
				if (!latinWithUnderscore.test(value)) {
					errors.reasonIdentifier = 'Identifier must be Latin letters, numbers, and underscores only (e.g., Cascade_2025_1_dep).';
				} else {
					delete errors.reasonIdentifier;
				}
				break;
			case 'bonusAmount':
				if (Number(bonusesAddModal.bonusMode) === 1) {
					delete errors.bonusAmount;
					break;
				}
				const amount = parseFloat(value);
				if (isNaN(amount) || amount > 12000) {
					errors.bonusAmount = 'Bonus Amount must be a number and not exceed 12000 RUB.';
				} else {
					delete errors.bonusAmount;
				}
				break;
			case 'bonusMode':
				if (Number(value) === 1) {
					delete errors.bonusAmount;
				} else {
					delete errors.depositPercent;
					delete errors.depositApplyLimit;
					delete errors.minDeposit;
					delete errors.maxDeposit;
					delete errors.maxBonusAmount;
				}
				break;
			case 'depositPercent':
				if (Number(bonusesAddModal.bonusMode) !== 1) {
					delete errors.depositPercent;
					break;
				}
				break;
			case 'depositApplyLimit':
				if (Number(bonusesAddModal.bonusMode) !== 1) {
					delete errors.depositApplyLimit;
					break;
				}
				const applyLimit = Number(value);
				if (!Number.isInteger(applyLimit) || applyLimit <= 0) {
					errors.depositApplyLimit = 'Deposit Apply Limit must be a whole number greater than 0.';
				} else {
					delete errors.depositApplyLimit;
				}
				break;
			case 'minDeposit':
				if (Number(bonusesAddModal.bonusMode) !== 1) {
					delete errors.minDeposit;
					break;
				}
				const minDeposit = parseFloat(value);
				if (isNaN(minDeposit) || minDeposit < 0) {
					errors.minDeposit = 'Min Deposit must be a non-negative number.';
				} else {
					delete errors.minDeposit;
				}
				break;
			case 'maxDeposit':
				if (Number(bonusesAddModal.bonusMode) !== 1) {
					delete errors.maxDeposit;
					break;
				}
				const maxDeposit = parseFloat(value);
				if (isNaN(maxDeposit) || maxDeposit < 0) {
					errors.maxDeposit = 'Max Deposit must be a non-negative number.';
				} else if (parseFloat(bonusesAddModal.minDeposit) > maxDeposit) {
					errors.maxDeposit = 'Max Deposit must be greater than or equal to Min Deposit.';
				} else {
					delete errors.maxDeposit;
				}
				break;
			case 'maxBonusAmount':
				if (Number(bonusesAddModal.bonusMode) !== 1) {
					delete errors.maxBonusAmount;
					break;
				}
				const maxBonusAmount = parseFloat(value);
				if (isNaN(maxBonusAmount) || maxBonusAmount < 0) {
					errors.maxBonusAmount = 'Max Bonus amount must be a non-negative number.';
				} else {
					delete errors.maxBonusAmount;
				}
				break;
			case 'maxWithdrawal':
				const withdrawal = parseFloat(value);
				if (isNaN(withdrawal) || withdrawal < 100 || withdrawal > 50000) {
					errors.maxWithdrawal = 'Max Withdrawal must be between 100 and 50000 RUB.';
				} else {
					delete errors.maxWithdrawal;
				}
				break;
			case 'count':
				const activations = parseInt(value, 10);
				if (isNaN(activations) || activations > 10000) {
					errors.maxActivations = 'Max Activations must be a number and not exceed 10000.';
				} else {
					delete errors.maxActivations;
				}
				break;
			case 'depositFrom':
				const depositFrom = parseFloat(value);
				if (value && (isNaN(depositFrom) || depositFrom < 0)) {
					errors.depositFrom = 'Deposit From must be a non-negative number.';
				} else {
					delete errors.depositFrom;
				}
				break;
			case 'depositTo':
				const depositTo = parseFloat(value);
				if (value && (isNaN(depositTo) || depositTo < 0)) {
					errors.depositTo = 'Deposit To must be a non-negative number.';
				} else if (value && parseFloat(bonusesAddModal.depositFrom) > depositTo) {
					errors.depositTo = 'Deposit To must be greater than or equal to Deposit From.';
				} else {
					delete errors.depositTo;
				}
				break;
			case 'otherPromoAmount':
				const promoAmount = parseFloat(value);
				if (bonusesAddModal.otherPromoActivated && (isNaN(promoAmount) || promoAmount <= 0)) {
					errors.otherPromoAmount = 'Amount must be a positive number.';
				} else {
					delete errors.otherPromoAmount;
				}
				break;
			case 'otherPromoDays':
				const promoDays = parseInt(value, 10);
				if (bonusesAddModal.otherPromoActivated && (isNaN(promoDays) || promoDays <= 0)) {
					errors.otherPromoDays = 'Days must be a positive number.';
				} else {
					delete errors.otherPromoDays;
				}
				break;
			case 'activation_lifetime_days':
				const activationLifetimeDaysError = this.getActivationLifetimeDaysError(value);
				if (activationLifetimeDaysError) {
					errors.activation_lifetime_days = activationLifetimeDaysError;
				} else {
					delete errors.activation_lifetime_days;
				}
				break;
			case 'betAmount':
				const betAmt = parseFloat(value);
				if (bonusesAddModal.considerBetAmount && (isNaN(betAmt) || betAmt <= 0)) {
					errors.betAmount = 'Bet Amount must be a positive number.';
				} else {
					delete errors.betAmount;
				}
				break;
			case 'betDays':
				const betDays = parseInt(value, 10);
				if (bonusesAddModal.considerBetAmount && (isNaN(betDays) || betDays <= 0)) {
					errors.betDays = 'Days must be a positive number.';
				} else {
					delete errors.betDays;
				}
				break;
			case 'vipLevel':
				const vip = parseInt(value, 10);
				if (bonusesAddModal.vipLevelMode && (isNaN(vip) || vip < 0)) {
					errors.vipLevel = 'VIP Level must be a non-negative number.';
				} else {
					delete errors.vipLevel;
				}
				break;
			default:
				break;
		}

		setTimeout(() => {
			setBonusesAddModal({ ...bonusesAddModal, errors });
		}, 0);
	};


	handleChange = (field, value) => {
		const { bonusesAddModal, setBonusesAddModal } = this.props;
		const updatedState = cloneDeep(bonusesAddModal);
		updatedState[field] = value;

		setBonusesAddModal(updatedState);

		setTimeout(() => {
			this.validateField(field, value);
			this.updateDescription();
		}, 0);
	};

	updateAdPayments = (updater) => {
		const { bonusesAddModal, setBonusesAddModal } = this.props;
		const currentPayments = normalizeAdPayments(bonusesAddModal.adPayments);
		const nextPayments = updater(cloneDeep(currentPayments));
		const normalizedPayments = normalizeAdPayments(nextPayments);

		setBonusesAddModal({
			...bonusesAddModal,
			adPayments: normalizedPayments,
			adCostTotal: calculateAdPaymentsTotal(normalizedPayments),
		});
	};

	handleAddAdPayment = () => {
		this.updateAdPayments((currentPayments) => [
			...currentPayments,
			createEmptyAdPayment(),
		]);
	};

	handleRemoveAdPayment = (index) => {
		this.updateAdPayments((currentPayments) =>
			currentPayments.filter((_, paymentIndex) => paymentIndex !== index)
		);
	};

	handleAdPaymentChange = (index, field, value) => {
		this.updateAdPayments((currentPayments) => {
			const nextPayments = currentPayments.length ? currentPayments : [createEmptyAdPayment()];
			nextPayments[index] = {
				...(nextPayments[index] || createEmptyAdPayment()),
				[field]: value,
			};

			return nextPayments;
		});
	};

	resetBonusModeFields = (extra = {}) => {
		const { bonusesAddModal, setBonusesAddModal } = this.props;
		const errors = { ...bonusesAddModal.errors };

		delete errors.depositPercent;
		delete errors.depositApplyLimit;
		delete errors.minDeposit;
		delete errors.maxDeposit;
		delete errors.maxBonusAmount;

		setBonusesAddModal({
			...bonusesAddModal,
			show: false,
			bonusMode: 0,
			depositPercent: '',
			depositApplyLimit: '',
			minDeposit: '',
			maxDeposit: '',
			maxBonusAmount: '',
			bonusType: { label: 'All', value: 'all' },
			errors,
			...extra,
		});
	};


	handleWagerChange = ({ detail }) => {
		const selectedWager = detail.selectedOption;
		const wagerValue = parseInt(selectedWager.value, 10);
		if (wagerValue < 30) {
			this.props.setBonusesAddModal({ ...this.props.bonusesAddModal, showWagerConfirmation: true, wager: selectedWager });
		} else {
			this.props.setBonusesAddModal({ ...this.props.bonusesAddModal, showWagerConfirmation: false, wager: selectedWager });
			setTimeout(() => {
				this.updateDescription();
			}, 0);
		}
	};

	confirmWager = () => {
		this.props.setBonusesAddModal({ ...this.props.bonusesAddModal, showWagerConfirmation: false });
		setTimeout(() => {
			this.updateDescription();
		}, 0);
	};

	cancelWager = () => {
		this.props.setBonusesAddModal({ ...this.props.bonusesAddModal, wager: null, showWagerConfirmation: false });
		setTimeout(() => {
			this.updateDescription();
		}, 0);
	};

	updateDescription = () => {
		const { setBonusesAddModal, bonusesAddModal } = this.props;


		let tag = bonusesAddModal.note
			? `${bonusesAddModal.note}`
			: '';
		const appendTagPart = (value) => {
			if (!value) {
				return;
			}

			tag = tag
				? `${tag}${tag[tag.length - 1] !== "_" ? "_" : ""}${value}`
				: value;
		};

		if (bonusesAddModal.wager?.value) {
			const wagerValue = bonusesAddModal.wager.value;
			appendTagPart(`wagerX${wagerValue}`);
		}

		if (bonusesAddModal.maxWithdrawal) {
			const maxWithdrawal = parseFloat(bonusesAddModal.maxWithdrawal);
			const formattedCap = maxWithdrawal >= 1000 ? `${maxWithdrawal / 1000}K` : maxWithdrawal.toString();
			appendTagPart(`cap${formattedCap}`);
		}

		const description = {
			Tag: tag,
			bonusCode: bonusesAddModal.bonusCode || 'Not specified',
			Amount: bonusesAddModal.bonusAmount ? `${bonusesAddModal.bonusAmount} RUB` : 'Not specified',
			Wager: bonusesAddModal.wager?.value ? `x${bonusesAddModal.wager.value}` : 'Not specified',
			'Max Withdrawal': bonusesAddModal.maxWithdrawal ? `${bonusesAddModal.maxWithdrawal} RUB` : 'Not specified',
			'Create Date': new Date().toISOString(),
		};


		setTimeout(() => {
			setBonusesAddModal({ ...bonusesAddModal, description });
		}, 0);
	};


	 handleSubmit = async () => {
		const { bonusesAddModal, setBonusesAddModal, Interface } = this.props;
		const body = {};
		const isPercentMode = Number(bonusesAddModal.bonusMode) === 1;


		if (bonusesAddModal.advancedSettingsEnabled) {
			if (bonusesAddModal.reason === null) {
				Interface.addNoty("Error", "Please select reason");
				return;
			}
		}

		const activationLifetimeDaysError = this.getActivationLifetimeDaysError(
			bonusesAddModal.activation_lifetime_days
		);
		if (activationLifetimeDaysError) {
			setBonusesAddModal({
				...bonusesAddModal,
				errors: {
					...bonusesAddModal.errors,
					activation_lifetime_days: activationLifetimeDaysError,
				},
			});
			return;
		}

		if (isPercentMode) {
			const errors = { ...bonusesAddModal.errors };
			let hasErrors = false;


			const applyLimit = Number(bonusesAddModal.depositApplyLimit);
			if (!Number.isInteger(applyLimit) || applyLimit <= 0) {
				errors.depositApplyLimit = 'Deposit Apply Limit must be a whole number greater than 0.';
				hasErrors = true;
			} else {
				delete errors.depositApplyLimit;
			}

			const minDeposit = parseFloat(bonusesAddModal.minDeposit);
			if (isNaN(minDeposit) || minDeposit < 0) {
				errors.minDeposit = 'Min Deposit must be a non-negative number.';
				hasErrors = true;
			} else {
				delete errors.minDeposit;
			}

			const maxDeposit = parseFloat(bonusesAddModal.maxDeposit);
			if (isNaN(maxDeposit) || maxDeposit < 0) {
				errors.maxDeposit = 'Max Deposit must be a non-negative number.';
				hasErrors = true;
			} else if (minDeposit > maxDeposit) {
				errors.maxDeposit = 'Max Deposit must be greater than or equal to Min Deposit.';
				hasErrors = true;
			} else {
				delete errors.maxDeposit;
			}

			const maxBonusAmount = parseFloat(bonusesAddModal.maxBonusAmount);
			if (isNaN(maxBonusAmount) || maxBonusAmount < 0) {
				errors.maxBonusAmount = 'Max Bonus amount must be a non-negative number.';
				hasErrors = true;
			} else {
				delete errors.maxBonusAmount;
			}

			setBonusesAddModal({ ...bonusesAddModal, errors });
			if (hasErrors) {
				return;
			}
		}

		for (const key in bonusesAddModal) {
			if (Object.prototype.hasOwnProperty.call(bonusesAddModal, key)) {
				const value = bonusesAddModal[key];

				if (key === 'bonusType') {
					if (isPercentMode && value && typeof value === 'object' && 'value' in value) {
						body.bonusType = value.value;
					}
				}
				else if (value && typeof value === 'object' && 'value' in value && 'label' in value) {
					body[key] = value.value;
				}
				else if (key === 'type' && value && typeof value === 'object' && 'type' in value) {
					body[key] = value.type;
				}
				else if (key === 'bonusMode') {
					body.bonusMode = Number(value);
				}
				else if (key === 'bonusAmount') {
					if (!isPercentMode) {
						body['value'] = Number(value);
					}
				}
				else if (key === 'depositPercent') {
					if (isPercentMode) {
						body.depositPercent = Number(value);
					}
				}
				else if (key === 'depositApplyLimit') {
					if (isPercentMode) {
						body.depositApplyLimit = Number(value);
					}
				}
				else if (key === 'minDeposit') {
					if (isPercentMode) {
						body.minDeposit = Number(value);
					}
				}
				else if (key === 'maxDeposit') {
					if (isPercentMode) {
						body.maxDeposit = Number(value);
					}
				}
				else if (key === 'activation_lifetime_days') {
					if (value !== '' && value !== null) {
						body.activationLifeTimeDays = Number(value);
					}
				}
				else if (key === 'maxBonusAmount') {
					if (isPercentMode) {
						body.maxBonusAmount = Number(value);
					}
				}
				else if (key === 'uid' && value) {
					if (value.toString().includes(',')) {
						if (value[value.length-1] === ",") {
							Interface.addNoty("Error", "Uid not exists ',' in filed");
							return;
						}
						body['uid'] = value.split(',').map((element) => Number(element));
					} else {
						body['uid'] = value;
					}
				}
				else if (key === 'trafficType') {
					if (value) {
						body.traffic_type = value;
					}
				}
				else if (key === 'trafficSource') {
					if (value) {
						body.traffic_source = value;
					}
				}
				else if (key === 'adPayments') {
					const adPayments = normalizeAdPayments(value)
						.filter((payment) => payment.date || payment.amount !== '')
						.map((payment) => ({
							date: payment.date || '',
							amount: payment.amount === '' ? '' : Number(payment.amount),
						}));
					const adPaymentsTotal = calculateAdPaymentsTotal(value);

					if (adPayments.length) {
						body.ad_payments_json = adPayments;
						body.ad_cost_total = Number(adPaymentsTotal);
					}
				}
				else if ( key === "description" && value && typeof value === 'object' && 'Tag' in value ) {
					body["note"] = value.Tag;
				}
				else {
					if (value)
					body[key] = value;
				}
			}
		}



		this.setState({ loading: true });
		try {
			let iteration = bonusesAddModal.promoCountGeneration === 0 || !bonusesAddModal.isPublic ? 1 : bonusesAddModal.promoCountGeneration;
			for (let i = 0; i < iteration; i++) {
				const result = await Interface.request("set", {
					class: "data",
					module: "bonuses",
					method: "add"
				}, body);
			}

			Interface.addNoty("Success", "Promo was successfully added");
			this.resetBonusModeFields();
			Interface.loadBonuses();
		} catch (error) {
			Interface.addNoty("Error", error.text || error.toString());
		} finally {
			this.setState({ loading: false });
		}
	};

	render() {
		const { setBonusesAddModal, bonusesAddModal } = this.props;


		const noteReason = {
			"compensation_for_problem_payments": "sorry",
			"reactivation_vip": "return",
			"reactivation_email": "return",
			"welcome_bonus": "wel",
			"first_deposit": "dep1",
			"second_deposit": "dep2",
			"third_deposit": "dep3",
			"fourth_deposit": "dep4",
			"fifth_deposit": "dep5",
			"holiday": "promo_holiday",
			"attraction_slots": "promo_slots",
			"Attraction to tournaments": "promo_turnir",
			"promotion": "promotion",
			"email_promo": "email_promo",
			"attraction_tournaments": "attraction_tournaments",
			"affilate": "affilate"
		};

		const reasons = [
			{ label: 'Compensation for problem payments + date', value: 'compensation_for_problem_payments' },
			{ label: 'Reactivation VIP + date', value: 'reactivation_vip' },
			{ label: 'Reactivation email + date', value: 'reactivation_email' },
			{ label: 'Welcome bonus', value: 'welcome_bonus' },
			{ label: 'First deposit', value: 'first_deposit' },
			{ label: 'Second deposit', value: 'second_deposit' },
			{ label: 'Third deposit', value: 'third_deposit' },
			{ label: 'Fourth deposit', value: 'fourth_deposit' },
			{ label: 'Fifth deposit', value: 'fifth_deposit' },
			{ label: 'Holiday', value: 'holiday' },
			{ label: 'Attraction to slots', value: 'attraction_slots' },
			{ label: 'Email Promo', value: 'email_promo' },
			{ label: 'Promotion', value: 'promotion' },
			{ label: 'Attraction to tournaments', value: 'attraction_tournaments' },
			{ label: 'Affilate', value: 'affilate' },
		];

		const wagerOptions = Array.from({ length: 8 }, (_, i) => ({
			label: `x${(i + 1) * 10}`,
			value: `${(i + 1) * 10}`,
		}));

		const vipLevelModes = [
			{ label: 'All', value: 'all' },
			{ label: 'Equal to', value: 'equal' },
			{ label: 'Above', value: 'above' },
			{ label: 'Below', value: 'below' },
		];

		const typeOptions = this.props.config.bonuses?.options?.type || [
			{ label: 'Public', value: 'public', type: 0 },
			{ label: 'Private', value: 'private', type: 1 },
		];

		const bonusModeOptions = [
			{ label: 'Fixed amount', value: 0 },
			{ label: 'Deposit percent', value: 1 },
		].filter((element) => !bonusesAddModal.advancedSettingsEnabled ? element.value === 0 : element);
		const bonusTypeOptions = [
			{ label: 'Fiat', value: 'fiat' },
			{ label: 'Crypto', value: 'crypto' },
			{ label: 'All', value: 'all' },
		];

		const selectedBonusMode =
			bonusModeOptions.find((option) => Number(option.value) === Number(bonusesAddModal.bonusMode))
			|| bonusModeOptions[0];
		const selectedBonusType =
			bonusTypeOptions.find((option) => option.value === bonusesAddModal.bonusType?.value)
			|| bonusTypeOptions[2];
		const adPayments = normalizeAdPayments(bonusesAddModal.adPayments);
		const calculatedAdCostTotal = calculateAdPaymentsTotal(adPayments);
		const finalTag = bonusesAddModal.description?.Tag || bonusesAddModal.note;
		const tagDescription = finalTag && finalTag !== bonusesAddModal.note
			? `Enter a unique identifier for the reason (e.g., Cascade_2025_1_dep). Use Latin letters, numbers, and underscores only. Final tag sent to backend: ${finalTag}`
			: "Enter a unique identifier for the reason (e.g., Cascade_2025_1_dep). Use Latin letters, numbers, and underscores only.";
		const showAffiliateSimpleFields = bonusesAddModal?.type?.type === 1 && !bonusesAddModal.advancedSettingsEnabled;
		const showWagerAndMaxWithdrawalFields = bonusesAddModal.advancedSettingsEnabled || showAffiliateSimpleFields;
		const displayedAdCostTotal =
			calculatedAdCostTotal !== ''
				? calculatedAdCostTotal
				: (bonusesAddModal.adCostTotal ?? '');


			console.log("ERROR", bonusesAddModal);
		return (
			 <>
				<Modal
					onDismiss={this.resetBonusModeFields}
					visible={bonusesAddModal.show}
					size="max"
					header="Create Bonus Code"
					footer={
						<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button onClick={this.resetBonusModeFields}>Cancel</Button>
							<Button variant="primary" onClick={this.handleSubmit} disabled={Object.keys(bonusesAddModal.errors).length > 0 || this.state.loading}>
								Create
							</Button>
						</SpaceBetween>
						</Box>
					}
				>
					{this.state.loading ? 	<Box padding={{ top: "xxxl", bottom: "xxxl" }} children={<Box textAlign="center" padding={{ top: "xxxl", bottom: "xxxl" }} margin={{ top: "xxxl", bottom: "xxxl" }} children={<Spinner size="big" />} />} /> : <Form>
						<SpaceBetween direction="vertical" size="l">
							<Container header={<Box variant="h2">Basic Settings</Box>}>
								<SpaceBetween direction="vertical" size="m">
									<FormField
										stretch
										label="Promocode Type"
										description="Select whether the promo code is public (mass activation) or private (specific players only)."
									>
										<Select
											filteringType="auto"
											invalid={
												typeof bonusesAddModal.type.type === 'undefined' && bonusesAddModal.checked
													? true
													: false
											}
											disabled={!this.props.bonuses.loaded}
											placeholder="Type"
											selectedOption={this.props.bonusesAddModal.type}
											onChange={(option) => {
												const isPublic = option.detail.selectedOption.type === 0;
												const errors = { ...bonusesAddModal.errors };

												delete errors.wager;
												delete errors.maxWithdrawal;
												delete errors.reasonIdentifier;

												setBonusesAddModal({
													...bonusesAddModal,
													isPublic,
													type: option.detail.selectedOption,
													count: option.detail.selectedOption.type === 2 ? 1 : bonusesAddModal.count,
													note: '',
													description: {},
													wager: null,
													maxWithdrawal: '',
													showWagerConfirmation: false,
													errors,
												});
												setTimeout(() => {
													this.updateDescription();
												}, 0);
											}}
											options={typeOptions}
										/>
									</FormField>

									{!bonusesAddModal.isPublic && (
										<FormField
											stretch
											label="Player IDs"
											description="Enter the player IDs allowed to activate this promo code (e.g., 123, 456, 789). Separate multiple IDs with commas or spaces."
										>
											<Input
												value={bonusesAddModal.uid}
												onChange={({ detail }) => this.handleChange('uid', detail.value)}
												placeholder="e.g., 123, 456, 789"
											/>
										</FormField>
									)}



									{bonusesAddModal.advancedSettingsEnabled && <FormField
										stretch
										label="Reason for Issuance"
										description="Select the reason for issuing this bonus code for analytics parsing."
										errorText={bonusesAddModal.errors.reason}
									>
										<Select
											selectedOption={bonusesAddModal.reason}
											onChange={({ detail }) =>
											{
												this.handleChange('reason', detail.selectedOption);
												setTimeout(() => {
													this.handleChange('note', noteReason[detail.selectedOption.value]);
												}, 100);
											}}
											options={reasons}
											placeholder="Select a reason"
										/>
									</FormField>}


									{(bonusesAddModal.advancedSettingsEnabled === false || bonusesAddModal.reason) && (
										<FormField
											stretch
											label="Tag"
											description={tagDescription}
											errorText={bonusesAddModal.errors.reasonIdentifier}
										>
											<Input
												value={bonusesAddModal.note}
												onChange={({ detail }) => this.handleChange('note', detail.value)}
												placeholder="e.g., Cascade_2025_1_dep"
											/>
										</FormField>
									)}

									<FormField
										label="Advanced Settings"
										description="Enable to configure additional promo code settings such as wager, deposit requirements, and restrictions."
									>
										<Checkbox
											checked={bonusesAddModal.advancedSettingsEnabled || false}
											onChange={({ detail }) => {
												this.handleChange('advancedSettingsEnabled', detail.checked);
											}}
										>
											Enable advanced settings
										</Checkbox>
									</FormField>
								</SpaceBetween>
							</Container>

							<Container header={<Box variant="h2">Code Details</Box>}>
								<SpaceBetween direction="vertical" size="m">
									{bonusesAddModal.advancedSettingsEnabled && <FormField
										stretch
										label="BonusCode"
										description="Enter a descriptive name for the bonusCode (e.g., FirstDeposit2025). Max 20 characters, Latin letters and numbers only, no spaces."
										errorText={bonusesAddModal.errors.bonusCode}
									>
										<Input
											value={bonusesAddModal.bonusCode}
											onChange={({ detail }) => this.handleChange('bonusCode', detail.value)}
											placeholder="e.g., FirstDeposit2025"
										/>
									</FormField>}

									<FormField
										stretch
										label="Name (Player-Facing)"
										description="Enter the promo code name visible to players (e.g., NYFS). Max 16 characters, Latin letters and numbers only, no spaces."
										errorText={bonusesAddModal.errors.name}
									>
										<Input
											value={bonusesAddModal.name}
											onChange={({ detail }) => this.handleChange('name', detail.value)}
											placeholder="e.g., NYFS"
										/>
									</FormField>

									<FormField
										stretch
										label="Bonus Mode"
										description="Choose how the deposit bonus is granted."
									>
										<Select
											selectedOption={selectedBonusMode}
											onChange={({ detail }) => this.handleChange('bonusMode', detail.selectedOption.value)}
											options={bonusModeOptions}
											placeholder="Select mode"
										/>
									</FormField>

									{(Number(bonusesAddModal.bonusMode) === 1 && bonusesAddModal.advancedSettingsEnabled) ? (
										<>
											<FormField
												stretch
												label="Deposit Percent (%)"
												description="Enter the bonus percent for deposit"
												errorText={bonusesAddModal.errors.depositPercent}
											>
												<Input
													type="number"
													value={bonusesAddModal.depositPercent}
													onChange={({ detail }) => this.handleChange('depositPercent', detail.value)}
													placeholder="e.g., 50"
												/>
											</FormField>

											<FormField
												stretch
												label="Deposit Apply Limit (Deposits)"
												description="Enter the number of deposits this bonus applies to."
												errorText={bonusesAddModal.errors.depositApplyLimit}
											>
												<Input
													type="number"
													value={bonusesAddModal.depositApplyLimit}
													onChange={({ detail }) => this.handleChange('depositApplyLimit', detail.value)}
													placeholder="e.g., 3"
												/>
											</FormField>

											<FormField
												stretch
												label="Min Deposit (RUB)"
												description="Enter the minimum deposit amount required in RUB. Use 0 to indicate no deposit requirement."
												errorText={bonusesAddModal.errors.minDeposit}
											>
												<Input
													type="number"
													value={bonusesAddModal.minDeposit}
													onChange={({ detail }) => this.handleChange('minDeposit', detail.value)}
													placeholder="e.g., 300"
													inputMode="numeric"
												/>
											</FormField>

											<FormField
												stretch
												label="Max Deposit (RUB)"
												description="Enter the maximum deposit amount required in RUB. Must be greater than or equal to Min Deposit."
												errorText={bonusesAddModal.errors.maxDeposit}
											>
												<Input
													type="number"
													value={bonusesAddModal.maxDeposit}
													onChange={({ detail }) => this.handleChange('maxDeposit', detail.value)}
													placeholder="e.g., 1000"
													inputMode="numeric"
												/>
											</FormField>

											<FormField
												stretch
												label="Max Bonus amount"
												description="Enter the maximum bonus amount in RUB for subsequent deposits."
												errorText={bonusesAddModal.errors.maxBonusAmount}
											>
												<Input
													type="number"
													value={bonusesAddModal.maxBonusAmount}
													onChange={({ detail }) => this.handleChange('maxBonusAmount', detail.value)}
													placeholder="e.g., 5000"
													inputMode="numeric"
												/>
											</FormField>

											<FormField
												stretch
												label="Bonus Type"
												description="Choose which deposit type this bonus applies to."
											>
												<Select
													selectedOption={selectedBonusType}
													onChange={({ detail }) => this.handleChange('bonusType', detail.selectedOption)}
													options={bonusTypeOptions}
													placeholder="Select bonus type"
												/>
											</FormField>
										</>
									) : (
										<FormField
											stretch
											label="Bonus Amount (RUB)"
											description="Enter the bonus amount in RUB. Maximum allowed is 12000 RUB."
											errorText={bonusesAddModal.errors.bonusAmount}
										>
											<Input
												type="number"
												value={bonusesAddModal.bonusAmount}
												onChange={({ detail }) => this.handleChange('bonusAmount', detail.value)}
												placeholder="e.g., 30"
											/>
										</FormField>
									)}

									{showWagerAndMaxWithdrawalFields && <FormField
										stretch
										label="Wager"
										description="Select the wager requirement (x10 to x80). A confirmation is required if less than x30."
										errorText={bonusesAddModal.errors.wager}
									>
										<Select
											selectedOption={bonusesAddModal.wager}
											onChange={this.handleWagerChange}
											options={wagerOptions}
											placeholder="Select wager"
										/>
									</FormField>}

									{showWagerAndMaxWithdrawalFields && <FormField
										stretch
										label="Max Withdrawal (RUB)"
										description="Enter the maximum withdrawal amount in RUB. Must be between 100 and 50000 RUB."
										errorText={bonusesAddModal.errors.maxWithdrawal}
									>
										<Input
											type="number"
											value={bonusesAddModal.maxWithdrawal}
											onChange={({ detail }) => this.handleChange('maxWithdrawal', detail.value)}
											placeholder="e.g., 5000"
										/>
									</FormField>}

									<FormField
										stretch
										label="Max Activations"
										description="Enter the maximum number of activations for this promo code. Maximum allowed is 10000."
										errorText={bonusesAddModal.errors.maxActivations}
									>
										<Input
											disabled={this.props.bonusesAddModal?.type?.type === 2}
											type="number"
											value={bonusesAddModal.count}
											onChange={({ detail }) => this.handleChange('count', detail.value)}
											placeholder="e.g., 1000"
										/>
									</FormField>

									{bonusesAddModal?.type?.type === 0 && <FormField
										stretch
										label="Promo count"
										description="Count promo generation."
									>
										<Input
											type="number"
											value={bonusesAddModal.promoCountGeneration}
											onChange={({ detail }) => this.handleChange('promoCountGeneration', detail.value)}
											placeholder="e.g., 5000"
										/>
									</FormField>}
								</SpaceBetween>
							</Container>

							{bonusesAddModal?.type?.type === 1 && bonusesAddModal.advancedSettingsEnabled && <Container header={<Box variant="h2">Affiliate Traffic Metadata</Box>}>
								<SpaceBetween direction="vertical" size="m">
									<FormField
										stretch
										label="Traffic Type"
										description="Optional partner traffic type metadata for this promo code."
									>
										<Input
											value={bonusesAddModal.trafficType}
											onChange={({ detail }) => this.handleChange('trafficType', detail.value)}
											placeholder="e.g., CPA, RevShare, Stream"
										/>
									</FormField>

									<FormField
										stretch
										label="Traffic Source"
										description="Optional partner traffic source metadata for this promo code."
									>
										<Input
											value={bonusesAddModal.trafficSource}
											onChange={({ detail }) => this.handleChange('trafficSource', detail.value)}
											placeholder="e.g., Telegram channel, YouTube, Website"
										/>
									</FormField>

									<FormField
										stretch
										label="Total Ad Cost"
										description="Automatically summed from Ad Payments History."
									>
										<Input
											type="number"
											value={displayedAdCostTotal}
											readOnly={true}
											placeholder="e.g., 50000"
										/>
									</FormField>

									<FormField
										label="Ad Payments History"
										description="Optional payment history rows stored inside ad_payments_json."
									>
										<SpaceBetween direction="vertical" size="s">
											{adPayments.length === 0 ? (
												<Box variant="p">No payment history entries added yet.</Box>
											) : (
												adPayments.map((payment, index) => (
													<Grid
														key={`ad-payment-${index}`}
														gridDefinition={[
															{ colspan: { default: 12, s: 4 } },
															{ colspan: { default: 12, s: 6 } },
															{ colspan: { default: 12, s: 2 } },
														]}
													>
														<FormField stretch label={`Payment Date ${index + 1}`}>
															<DatePicker
																value={payment.date || ''}
																onChange={({ detail }) =>
																	this.handleAdPaymentChange(index, 'date', detail.value)
																}
																placeholder="YYYY-MM-DD"
															/>
														</FormField>

														<FormField stretch label={`Payment Amount ${index + 1}`}>
															<Input
																type="number"
																value={payment.amount ?? ''}
																onChange={({ detail }) =>
																	this.handleAdPaymentChange(index, 'amount', detail.value)
																}
																placeholder="e.g., 15000"
															/>
														</FormField>

														<Box margin={{ top: "l" }} textAlign="right">
															<Button
																iconName="remove"
																onClick={() => this.handleRemoveAdPayment(index)}
															>
																Remove
															</Button>
														</Box>
													</Grid>
												))
											)}

											<Button iconName="add-plus" onClick={this.handleAddAdPayment}>
												Add payment
											</Button>
										</SpaceBetween>
									</FormField>
								</SpaceBetween>
							</Container>}

							{bonusesAddModal.advancedSettingsEnabled && <>
								<Container header={<Box variant="h2">Deposit Requirements</Box>}>
									<SpaceBetween direction="vertical" size="m">
										{Number(bonusesAddModal.bonusMode) !== 1 && (
											<>
												<FormField
													stretch
													label="Deposit From (RUB)"
													description="Enter the minimum deposit amount required in RUB. Use 0 to indicate no deposit requirement."
													errorText={bonusesAddModal.errors.depositFrom}
												>
													<Input
														type="number"
														value={bonusesAddModal.depositFrom}
														onChange={({ detail }) => this.handleChange('depositFrom', detail.value)}
														placeholder="e.g., 300"
														inputMode="numeric"
													/>
												</FormField>

												<FormField
													stretch
													label="Deposit To (RUB)"
													description="Enter the maximum deposit amount required in RUB. Must be greater than or equal to Deposit From."
													errorText={bonusesAddModal.errors.depositTo}
												>
													<Input
														type="number"
														value={bonusesAddModal.depositTo}
														onChange={({ detail }) => this.handleChange('depositTo', detail.value)}
														placeholder="e.g., 500"
														inputMode="numeric"
													/>
												</FormField>
											</>
										)}

										<FormField
											label="Deposits From Count"
											description="Select the required number of deposits for eligibility (e.g., 1st deposit only). Use 'No Requirement' to disable this condition."
											stretch
										>
											<Input
												type="number"
												value={bonusesAddModal.depositCount}
												onChange={({ detail }) => this.handleChange('depositCount', detail.value)}
												placeholder="e.g., 1"
												inputMode="numeric"
											/>
										</FormField>

										<FormField
											label="Deposits To Count"
											description="Select the required number of deposits for eligibility (e.g., 1st deposit only). Use 'No Requirement' to disable this condition."
											stretch
										>
											<Input
												type="number"
												value={bonusesAddModal.depositCountTo}
												onChange={({ detail }) => this.handleChange('depositCountTo', detail.value)}
												placeholder="e.g., 1"
												inputMode="numeric"
											/>
										</FormField>
										<SpaceBetween direction="horizontal" size="m">
											<FormField
												label="Deposit period date from/To"
												description="Specify the date range for the deposit period."
												stretch
											>
												<DateRangePicker
													onChange={({ detail }) => {
														setBonusesAddModal({
															...bonusesAddModal,
															lastDepositDateFrom: detail.value.startDate.split("+")[0],
															lastDepositDateTo: detail.value.endDate.split("+")[0],
															lastDepositDateType: detail.value.type,
														});
													}}
													value={{
														startDate: bonusesAddModal.lastDepositDateFrom,
														endDate: bonusesAddModal.lastDepositDateTo,
														type: "absolute",
													}}
													relativeOptions={[]}
													isValidRange={range => {
														if (range.type === "absolute") {
															const [startDateWithoutTime] = range.startDate.split("T");
															const [endDateWithoutTime] = range.endDate.split("T");
															if (!startDateWithoutTime || !endDateWithoutTime) {
																return {
																	valid: false,
																	errorMessage: "The selected date range is incomplete.",
																};
															}
															if (new Date(range.startDate) - new Date(range.endDate) > 0) {
																return {
																	valid: false,
																	errorMessage: "Start date must be before end date.",
																};
															}
														}
														return { valid: true };
													}}
													i18nStrings={{
														todayAriaLabel: "Today",
														nextMonthAriaLabel: "Next month",
														previousMonthAriaLabel: "Previous month",
														customRelativeRangeDurationLabel: "Duration",
														customRelativeRangeDurationPlaceholder: "Enter duration",
														customRelativeRangeOptionLabel: "Custom range",
														customRelativeRangeOptionDescription: "Define a custom range",
														customRelativeRangeUnitLabel: "Unit of time",
														formatRelativeRange: ({ startDate, endDate }) => {
															const start = new Date(startDate).toLocaleString("en-GB");
															const end = new Date(endDate).toLocaleString("en-GB");
															return `${start} - ${end}`;
														},
														formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
														dateTimeConstraintText: "Date and time must be within range.",
														renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) =>
															`Selected from ${startDate} to ${endDate}`,
														labelRelativeRange: "Relative range",
														labelAbsoluteRange: "Absolute range",
														dateTimePickerAriaLabel: "Date and time picker",
														dateTimePickerLabel: "Date and Time",
														clearButtonAriaLabel: "Clear date range",
														relativeModeTitle: "Relative mode",
														absoluteModeTitle: "Absolute mode",
														cancelButtonLabel: "Cancel",
														applyButtonLabel: "Apply",
													}}
												/>
											</FormField>




										</SpaceBetween>
										<FormField
											label="Deposits days"
											description="Select the required number of deposits for eligibility (e.g., 1st deposit days). Use 'No Requirement' to disable this condition."
											stretch
										>
											<Input
												type="number"
												value={bonusesAddModal.depositCountDays}
												onChange={({ detail }) => this.handleChange('depositCountDays', detail.value)}
												placeholder="e.g., 1"
												inputMode="numeric"
											/>
										</FormField>
									</SpaceBetween>
								</Container>
								<Container header={<Box variant="h2">Tasks Requirements</Box>}>
									<SpaceBetween direction="vertical" size="m">
										<FormField
											label="Tasks count"
											description="Select the required number of tasks for eligibility (e.g., 1st taskCount). Use 'No Requirement' to disable this condition."
											stretch
										>
											<Input
												type="number"
												value={bonusesAddModal.taskCount}
												onChange={({ detail }) => this.handleChange('taskCount', detail.value)}
												placeholder="e.g., 1"
												inputMode="numeric"
											/>
										</FormField>
										<FormField
											label="Tasks days"
											description="Select the required number of deposits for eligibility (e.g., 1st tasks days). Use 'No Requirement' to disable this condition."
											stretch
										>
											<Input
												type="number"
												value={bonusesAddModal.taskDays}
												onChange={({ detail }) => this.handleChange('taskDays', detail.value)}
												placeholder="e.g., 1"
												inputMode="numeric"
											/>
										</FormField>
									</SpaceBetween>
								</Container>
								<Container header={<Box variant="h2">Verification and Restrictions</Box>}>
									<SpaceBetween direction="vertical" size="m">
										<FormField
											label="Verification Requirements"
											description="Select the verification requirements for activating this promo code."
										>
											<SpaceBetween direction="horizontal" size="m">
												<Checkbox
													checked={bonusesAddModal.emailChecked}
													onChange={({ detail }) => this.handleChange('emailChecked', detail.checked)}
												>
													Email
												</Checkbox>
												<Checkbox
													checked={bonusesAddModal.phoneChecked}
													onChange={({ detail }) => this.handleChange('phoneChecked', detail.checked)}
												>
													Phone
												</Checkbox>
												<Checkbox
													checked={bonusesAddModal.tgChecked}
													onChange={({ detail }) => this.handleChange('tgChecked', detail.checked)}
												>
													TG
												</Checkbox>
												<Checkbox
													checked={bonusesAddModal.verifiedChecked}
													onChange={({ detail }) => this.handleChange('verifiedChecked', detail.checked)}
												>
													Verified
												</Checkbox>
											</SpaceBetween>
										</FormField>

										<FormField
											label="Other Promo Activated"
											description="Check if the player has activated another promo code within a specified amount and time period."
										>
											<SpaceBetween direction="horizontal" size="m">
												<Checkbox
													checked={bonusesAddModal.otherPromoActivated}
													onChange={({ detail }) => this.handleChange('otherPromoActivated', detail.checked)}
												>
													Check if another promo was activated
												</Checkbox>
												{bonusesAddModal.otherPromoActivated && (
													<>
														<Input
															type="number"
															value={bonusesAddModal.otherPromoAmount}
															onChange={({ detail }) => this.handleChange('otherPromoAmount', detail.value)}
															placeholder="Amount in RUB"
														/>
														<DateRangePicker
															onChange={({ detail }) => {
																setBonusesAddModal({
																	...bonusesAddModal,
																	otherPromoDateFrom: detail.value.startDate.split("+")[0],
																	otherPromoDateTo: detail.value.endDate.split("+")[0],
																	otherPromoDateType: detail.value.type,
																});
															}}
															value={{
																startDate: bonusesAddModal.otherPromoDateFrom,
																endDate: bonusesAddModal.otherPromoDateTo,
																type: "absolute",
															}}
															relativeOptions={[]}
															isValidRange={range => {
																if (range.type === "absolute") {
																	const [startDateWithoutTime] = range.startDate.split("T");
																	const [endDateWithoutTime] = range.endDate.split("T");
																	if (!startDateWithoutTime || !endDateWithoutTime) {
																		return {
																			valid: false,
																			errorMessage: "The selected date range is incomplete.",
																		};
																	}
																	if (new Date(range.startDate) - new Date(range.endDate) > 0) {
																		return {
																			valid: false,
																			errorMessage: "Start date must be before end date.",
																		};
																	}
																}
																return { valid: true };
															}}
															i18nStrings={{
																todayAriaLabel: "Today",
																nextMonthAriaLabel: "Next month",
																previousMonthAriaLabel: "Previous month",
																customRelativeRangeDurationLabel: "Duration",
																customRelativeRangeDurationPlaceholder: "Enter duration",
																customRelativeRangeOptionLabel: "Custom range",
																customRelativeRangeOptionDescription: "Define a custom range",
																customRelativeRangeUnitLabel: "Unit of time",
																formatRelativeRange: ({ startDate, endDate }) => {
																	const start = new Date(startDate).toLocaleString("en-GB");
																	const end = new Date(endDate).toLocaleString("en-GB");
																	return `${start} - ${end}`;
																},
																formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
																dateTimeConstraintText: "Date and time must be within range.",
																renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) =>
																	`Selected from ${startDate} to ${endDate}`,
																labelRelativeRange: "Relative range",
																labelAbsoluteRange: "Absolute range",
																dateTimePickerAriaLabel: "Date and time picker",
																dateTimePickerLabel: "Date and Time",
																clearButtonAriaLabel: "Clear date range",
																relativeModeTitle: "Relative mode",
																absoluteModeTitle: "Absolute mode",
																cancelButtonLabel: "Cancel",
																applyButtonLabel: "Apply",
															}}
														/>
													</>
												)}
											</SpaceBetween>
										</FormField>

										<FormField
											label="Consider Bet Amount"
											description="Check if the player's bet amount meets the specified criteria over a given period."
										>
											<SpaceBetween direction="horizontal" size="m">
												<Checkbox
													checked={bonusesAddModal.considerBetAmount}
													onChange={({ detail }) => this.handleChange('considerBetAmount', detail.checked)}
												>
													Yes
												</Checkbox>
												{bonusesAddModal.considerBetAmount && (
													<>
														<Input
															type="number"
															value={bonusesAddModal.betAmount}
															onChange={({ detail }) => this.handleChange('betAmount', detail.value)}
															placeholder="Bet Amount"
														/>
														<DateRangePicker
															onChange={({ detail }) => {
																setBonusesAddModal({
																	...bonusesAddModal,
																	betDateFrom: detail.value.startDate.split("+")[0],
																	betDateTo: detail.value.endDate.split("+")[0],
																	betDateType: detail.value.type,
																});
															}}
															value={{
																startDate: bonusesAddModal.betDateFrom,
																endDate: bonusesAddModal.betDateTo,
																type: "absolute",
															}}
															relativeOptions={[]}
															isValidRange={range => {
																if (range.type === "absolute") {
																	const [startDateWithoutTime] = range.startDate.split("T");
																	const [endDateWithoutTime] = range.endDate.split("T");
																	if (!startDateWithoutTime || !endDateWithoutTime) {
																		return {
																			valid: false,
																			errorMessage: "The selected date range is incomplete.",
																		};
																	}
																	if (new Date(range.startDate) - new Date(range.endDate) > 0) {
																		return {
																			valid: false,
																			errorMessage: "Start date must be before end date.",
																		};
																	}
																}
																return { valid: true };
															}}
															i18nStrings={{
																todayAriaLabel: "Today",
																nextMonthAriaLabel: "Next month",
																previousMonthAriaLabel: "Previous month",
																customRelativeRangeDurationLabel: "Duration",
																customRelativeRangeDurationPlaceholder: "Enter duration",
																customRelativeRangeOptionLabel: "Custom range",
																customRelativeRangeOptionDescription: "Define a custom range",
																customRelativeRangeUnitLabel: "Unit of time",
																formatRelativeRange: ({ startDate, endDate }) => {
																	const start = new Date(startDate).toLocaleString("en-GB");
																	const end = new Date(endDate).toLocaleString("en-GB");
																	return `${start} - ${end}`;
																},
																formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
																dateTimeConstraintText: "Date and time must be within range.",
																renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) =>
																	`Selected from ${startDate} to ${endDate}`,
																labelRelativeRange: "Relative range",
																labelAbsoluteRange: "Absolute range",
																dateTimePickerAriaLabel: "Date and time picker",
																dateTimePickerLabel: "Date and Time",
																clearButtonAriaLabel: "Clear date range",
																relativeModeTitle: "Relative mode",
																absoluteModeTitle: "Absolute mode",
																cancelButtonLabel: "Cancel",
																applyButtonLabel: "Apply",
															}}
														/>
													</>
												)}
											</SpaceBetween>
										</FormField>

										<FormField
											stretch
											label="VIP Level Requirement"
											description="Specify the VIP level requirement for eligibility (e.g., all levels, specific level, or range)."
										>
											<SpaceBetween direction="horizontal" size="m">
												<Select
													selectedOption={bonusesAddModal.vipLevelMode}
													onChange={({ detail }) => this.handleChange('vipLevelMode', detail.selectedOption)}
													options={vipLevelModes}
													placeholder="Select mode"
												/>
												{bonusesAddModal.vipLevelMode &&  bonusesAddModal.vipLevelMode?.value !== "all" && (
													<Select
														selectedOption={
															bonusesAddModal.vipLevel !== null
																? { value: bonusesAddModal.vipLevel, label: `Level ${bonusesAddModal.vipLevel}` }
																: null
														}
														onChange={({ detail }) =>
															this.handleChange('vipLevel', detail.selectedOption ? detail.selectedOption.value : null)
														}
														options={[...Array(10)].map((_, i) => ({
															value: i+1,
															label: `Level ${i+1}`,
														}))}
														placeholder="No VIP requirement"
													/>
												)}
											</SpaceBetween>
										</FormField>
									</SpaceBetween>
								</Container>


								<Container header={<Box variant="h2">Promo Settings</Box>}>
									<SpaceBetween direction="vertical" size="m">
										<FormField
											label="Promo Validity Period"
											description="Specify the date range during which the promo code is valid. It will deactivate after the 'Valid To' date."
										>
											<DateRangePicker
												onChange={({ detail }) => {
													setBonusesAddModal({
														...bonusesAddModal,
														promoDateFrom: detail.value.startDate.split("+")[0],
														promoDateTo: detail.value.endDate.split("+")[0],
														promoDateType: detail.value.type,
													});
												}}
												value={{
													startDate: bonusesAddModal.promoDateFrom,
													endDate: bonusesAddModal.promoDateTo,
													type: "absolute",
												}}
												relativeOptions={[]}
												isValidRange={range => {
													if (range.type === "absolute") {
														const [startDateWithoutTime] = range.startDate.split("T");
														const [endDateWithoutTime] = range.endDate.split("T");
														if (!startDateWithoutTime || !endDateWithoutTime) {
															return {
																valid: false,
																errorMessage: "The selected date range is incomplete.",
															};
														}
														if (new Date(range.startDate) - new Date(range.endDate) > 0) {
															return {
																valid: false,
																errorMessage: "Start date must be before end date.",
															};
														}
													}
													return { valid: true };
												}}
												i18nStrings={{
													todayAriaLabel: "Today",
													nextMonthAriaLabel: "Next month",
													previousMonthAriaLabel: "Previous month",
													customRelativeRangeDurationLabel: "Duration",
													customRelativeRangeDurationPlaceholder: "Enter duration",
													customRelativeRangeOptionLabel: "Custom range",
													customRelativeRangeOptionDescription: "Define a custom range",
													customRelativeRangeUnitLabel: "Unit of time",
													formatRelativeRange: ({ startDate, endDate }) => {
														const start = new Date(startDate).toLocaleString("en-GB");
														const end = new Date(endDate).toLocaleString("en-GB");
														return `${start} - ${end}`;
													},
													formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
													dateTimeConstraintText: "Date and time must be within range.",
													renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) =>
														`Selected from ${startDate} to ${endDate}`,
													labelRelativeRange: "Relative range",
													labelAbsoluteRange: "Absolute range",
													dateTimePickerAriaLabel: "Date and time picker",
													dateTimePickerLabel: "Date and Time",
													clearButtonAriaLabel: "Clear date range",
													relativeModeTitle: "Relative mode",
													absoluteModeTitle: "Absolute mode",
													cancelButtonLabel: "Cancel",
													applyButtonLabel: "Apply",
												}}
											/>
										</FormField>
									<FormField
										label="Life time promo days"
										description="Sets how many days the promo code remains active after its third successful activation."
										errorText={bonusesAddModal.errors.activation_lifetime_days}
									>
										<Input
											type="number"
											value={bonusesAddModal.activation_lifetime_days}
											onChange={({ detail }) => this.handleChange('activation_lifetime_days', detail.value)}
											placeholder="e.g., 7"
											inputMode="numeric"
										/>
									</FormField>

									</SpaceBetween>
								</Container>

								<Container header={<Box variant="h2">Description Preview</Box>}>
									<SpaceBetween direction="vertical" size="xs">
										{Object.entries(bonusesAddModal.description).map(([key, value]) => (
											<Box key={key}>
												<strong>{key}:</strong> {value}
											</Box>
										))}
									</SpaceBetween>
								</Container>

							</>}
						</SpaceBetween>
					</Form>}
				</Modal>

				{bonusesAddModal.showWagerConfirmation && (
					<Modal
						onDismiss={this.cancelWager}
						visible={bonusesAddModal.showWagerConfirmation}
						header="Confirm Wager Selection"
						footer={
							<SpaceBetween direction="horizontal" size="xs">
								<Button onClick={this.cancelWager}>Cancel</Button>
								<Button variant="primary" onClick={this.confirmWager}>
									Confirm
								</Button>
							</SpaceBetween>
						}
					>
						<Alert type="warning">
							Are you sure you want to select a wager of x{bonusesAddModal.wager?.value}?
						</Alert>
					</Modal>
				)}
			</>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const bonuses = useRecoilValue(bonusesStore);
		const setBonuses = useSetRecoilState(bonusesStore);
		const config = useRecoilValue(configStore);
		const setConfig = useSetRecoilState(configStore);
		const bonusesAddModal = useRecoilValue(bonusesAddModalStore);
		const setBonusesAddModal = useSetRecoilState(bonusesAddModalStore);
		return (
			<Component
				{...props}
				bonuses={bonuses}
				setBonuses={setBonuses}
				setConfig={setConfig}
				config={config}
				bonusesAddModal={bonusesAddModal}
				setBonusesAddModal={setBonusesAddModal}
			/>
		);
	};
}

export default withRecoil(BonusesAddModal);